import { useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { BookOpen, CaretDown, CaretUp, ArrowSquareOut } from '@phosphor-icons/react';
import type { DeepResearchResult } from '@researcher/shared';

interface ResearchCardProps {
  result: DeepResearchResult;
}

export default function ResearchCard({ result }: ResearchCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showCitations, setShowCitations] = useState(false);

  const evidenceEntries = Object.entries(result.evidenceSummary.evidenceBreakdown || {})
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-pink-200 dark:border-pink-800 overflow-hidden my-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-50 to-orange-50 dark:from-pink-950/30 dark:to-orange-950/30 px-4 py-3 border-b border-pink-100 dark:border-pink-900">
        <div className="flex items-center gap-2">
          <BookOpen size={18} weight="fill" className="text-pink-500" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            What the Research Shows
          </h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Based on {result.evidenceSummary.totalSources} peer-reviewed studies
          {result.evidenceSummary.dateRange ? ` · ${result.evidenceSummary.dateRange}` : ''}
        </p>
      </div>

      {/* Evidence breakdown pills */}
      {evidenceEntries.length > 0 && (
        <div className="px-4 py-2 flex gap-1.5 flex-wrap border-b border-gray-100 dark:border-gray-800">
          {evidenceEntries.map(([type, count]) => (
            <span
              key={type}
              className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
            >
              {type.replace(/-/g, ' ')}: {count}
            </span>
          ))}
        </div>
      )}

      {/* Synthesis content */}
      <div className="px-4 py-3">
        <div className={`text-xs text-gray-700 dark:text-gray-300 leading-relaxed prose prose-xs max-w-none prose-headings:text-sm prose-headings:font-semibold prose-headings:text-gray-800 dark:prose-headings:text-gray-100 prose-headings:mt-3 prose-headings:mb-1 prose-p:mb-1 prose-p:text-xs prose-li:text-xs prose-li:mb-0.5 prose-strong:text-gray-800 dark:prose-strong:text-gray-100 prose-a:text-pink-600 dark:prose-a:text-pink-400 ${expanded ? '' : 'max-h-64 overflow-hidden'}`}>
          <ReactMarkdown>{result.synthesis}</ReactMarkdown>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-pink-500 hover:text-pink-600 mt-2 font-medium"
        >
          {expanded ? (
            <>Show less <CaretUp size={12} /></>
          ) : (
            <>Read full synthesis <CaretDown size={12} /></>
          )}
        </button>
      </div>

      {/* Confidence note */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
        <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
          {result.confidenceNote}
        </p>
      </div>

      {/* Citations */}
      {result.citations.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={() => setShowCitations(!showCitations)}
            className="w-full px-4 py-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
          >
            <span>{result.citations.length} citations</span>
            {showCitations ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </button>

          {showCitations && (
            <div className="px-4 pb-3 space-y-1.5">
              {result.citations.map((c, i) => (
                <div key={c.pubmedId || i} className="flex items-start gap-2 text-[11px]">
                  <span className="text-gray-400 flex-shrink-0">[{i + 1}]</span>
                  <div className="flex-1 min-w-0">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-600 dark:text-pink-400 hover:underline line-clamp-1"
                    >
                      {c.title}
                    </a>
                    <span className="text-gray-400 ml-1">
                      — {c.authors}, {c.journal} ({c.year})
                    </span>
                  </div>
                  <a href={c.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                    <ArrowSquareOut size={12} className="text-gray-400 hover:text-pink-500" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Related topics */}
      {result.relatedTopicSlugs.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800">
          <p className="text-[10px] text-gray-400 mb-1">Related library topics:</p>
          <div className="flex gap-1.5 flex-wrap">
            {result.relatedTopicSlugs.map(slug => (
              <Link
                key={slug}
                to={`/research/topics/${slug}`}
                className="text-[10px] px-2 py-0.5 rounded bg-pink-50 dark:bg-pink-950 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900 transition-colors"
              >
                {slug.replace(/-/g, ' ')}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
